import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import {
  Button,
  Space,
  message,
  Tag,
  Popconfirm,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { taskTemplateService, TaskTemplate } from '../services/taskTemplates';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { useListingTableFocus } from '../hooks/useListingTableFocus';
import { useListingColumnsPersistence } from '../hooks/useListingColumnsPersistence';
import { useListingColumnWidthsPersistence } from '../hooks/useListingColumnWidthsPersistence';
import { useListingSortPersistence } from '../hooks/useListingSortPersistence';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';
import {
  Table as FlowbiteTable,
  TableHead,
  TableHeadCell,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
} from 'flowbite-react';
import { Resizable } from 'react-resizable';

type LayoutHeaderContext = {
  setHeaderContent: (content: React.ReactNode) => void;
};

type SortOrder = 'ascend' | 'descend' | null;

const formatDateValue = (date?: string) => (date ? dayjs(date).format('DD/MM/YYYY') : '');

interface FlowbiteResizableHeaderProps {
  width?: number | string;
  maxWidth?: number;
  onResize?: (e: any, data: { size: { width: number } }) => void;
  onResizeStop?: (e: any, data: { size: { width: number } }) => void;
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
}

const FlowbiteResizableHeader: React.FC<FlowbiteResizableHeaderProps> = ({
  width,
  maxWidth,
  onResize,
  onResizeStop,
  onClick,
  className,
  children,
}) => {
  if (typeof width !== 'number' || !onResize) {
    return (
      <TableHeadCell
        className={className}
        style={width ? { width } : undefined}
        onClick={onClick}
      >
        {children}
      </TableHeadCell>
    );
  }

  return (
    <Resizable
      width={width}
      height={0}
      onResize={(e, data) => {
        const newWidth = Math.max(data.size.width, 50);
        onResize(e, { ...data, size: { ...data.size, width: newWidth } });
      }}
      onResizeStop={(e, data) => {
        if (!onResizeStop) {
          return;
        }
        const newWidth = Math.max(data.size.width, 50);
        onResizeStop(e, { ...data, size: { ...data.size, width: newWidth } });
      }}
      resizeHandles={['e']}
      handle={
        <span
          className="resizable-table-handle"
          style={{ height: '100%' }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        />
      }
      draggableOpts={{ enableUserSelectHack: false }}
      maxConstraints={typeof maxWidth === 'number' ? [maxWidth, 0] : undefined}
      minConstraints={[50, 0]}
    >
      <TableHeadCell
        className={className ? `relative ${className}` : 'relative'}
        style={{ width }}
        onClick={onClick}
      >
        {children}
      </TableHeadCell>
    </Resizable>
  );
};

export const TaskTemplates: React.FC = () => {
  const listingKey = 'task_templates';
  const [taskTemplates, setTaskTemplates] = useState<TaskTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [excludedRowKeys, setExcludedRowKeys] = useState<React.Key[]>([]);
  const [deleting, setDeleting] = useState(false);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);
  const [columnWidthByKey, setColumnWidthByKey] = useState<Record<string, number>>({});
  const tableScrollRef = useRef<HTMLDivElement | null>(null);
  const perPage = 200;
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const [availableWidth, setAvailableWidth] = useState<number | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const navigate = useNavigate();
  const { setHeaderContent } = useOutletContext<LayoutHeaderContext>();
  const { user } = useAuth();
  const canView = hasPermission(user, 'task_template.view');
  const canCreate = hasPermission(user, 'task_template.create');
  const canDelete = hasPermission(user, 'task_template.delete');


  useEffect(() => {
    if (!canView) {
      setTaskTemplates([]);
      return;
    }
    loadTaskTemplates(1, 'reset');
  }, [canView]);

  useEffect(() => {
    setHeaderContent(
      <span style={{ fontSize: 16, fontWeight: 600, color: '#111827' }}>
        Task Templates
      </span>
    );
    return () => setHeaderContent(null);
  }, [setHeaderContent]);

  const loadTaskTemplates = useCallback(async (pageToLoad: number, mode: 'reset' | 'append') => {
    if (!canView) {
      return;
    }
    if (mode === 'reset') {
      setLoading(true);
      setPage(1);
      setHasMore(true);
      loadingMoreRef.current = false;
    } else {
      if (loadingMoreRef.current) {
        return;
      }
      loadingMoreRef.current = true;
      setLoadingMore(true);
    }
    try {
      const response = await taskTemplateService.getAll({ page: pageToLoad, per_page: perPage });
      const { items, total, currentPage, lastPage } = normalizeTaskTemplatesResponse(response);
      setTaskTemplates((prev) => (mode === 'append' ? [...prev, ...items] : items));
      const resolvedTotal = typeof total === 'number' && total > 0 ? total : items.length;
      setTotalCount(resolvedTotal);
      setPage(currentPage || pageToLoad);
      if (lastPage && currentPage) {
        setHasMore(currentPage < lastPage);
      } else {
        setHasMore(items.length >= perPage);
      }
    } catch {
      message.error('Failed to load task templates data');
    } finally {
      if (mode === 'reset') {
        setLoading(false);
      } else {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [canView, perPage]);

  const normalizeTaskTemplatesResponse = (response: any) => {
    const raw = response?.data ?? response;
    const meta = raw?.meta;
    const payload = raw?.data && !Array.isArray(raw?.data) ? raw.data : raw;
    const items = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.data)
        ? payload.data
        : [];
    const total = Number(payload?.total ?? meta?.total ?? 0);
    const currentPage = Number(payload?.current_page ?? 0);
    const lastPage = Number(payload?.last_page ?? 0);
    const nextPageExists = false;
    return { items, nextPageExists, total, currentPage, lastPage };
  };

  const handleCreate = () => {
    navigate('/task-templates/new');
  };

  const handleEdit = (taskTemplate: TaskTemplate) => {
    if (!canView) {
      return;
    }
    navigate(`/task-templates/${taskTemplate.id}/edit`);
  };

  const handleBatchDelete = async () => {
    if (!selectAll && selectedRowKeys.length === 0) {
      return;
    }
    setDeleting(true);
    try {
      const payload = selectAll
        ? { select_all: true, excluded_ids: excludedRowKeys.map((key) => Number(key)) }
        : { ids: selectedRowKeys.map((key) => Number(key)) };
      await taskTemplateService.batchDelete(payload);
      message.success('Task templates deleted');
      setSelectedRowKeys([]);
      setSelectAll(false);
      setExcludedRowKeys([]);
      loadTaskTemplates(1, 'reset');
    } catch {
      message.error('Failed to delete task templates');
    } finally {
      setDeleting(false);
    }
  };


  const baseColumns = useMemo(
    () => [
    { 
      title: 'Name', 
      dataIndex: 'name', 
      key: 'name', 
      width: 200,
      sorter: (a: TaskTemplate, b: TaskTemplate) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Task Type',
      dataIndex: 'task_type',
      key: 'task_type',
      width: 150,
      render: (taskType: any, record: TaskTemplate) => {
        // Backend returns 'task_type' (snake_case), but TypeScript interface uses 'taskType' (camelCase)
        const type = taskType || record.taskType || (record as any).task_type;
        return type?.name || '-';
      },
      sorter: (a: TaskTemplate, b: TaskTemplate) => {
        const aType = a.taskType || (a as any).task_type;
        const bType = b.taskType || (b as any).task_type;
        const aName = aType?.name || '';
        const bName = bType?.name || '';
        return aName.localeCompare(bName);
      },
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 120,
      sorter: (a: TaskTemplate, b: TaskTemplate) => (a.enabled ? 1 : 0) - (b.enabled ? 1 : 0),
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'red'}>{enabled ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      sorter: (a: TaskTemplate, b: TaskTemplate) => {
        if (!a.created_at && !b.created_at) return 0;
        if (!a.created_at) return 1;
        if (!b.created_at) return -1;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      },
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      sorter: (a: TaskTemplate, b: TaskTemplate) => {
        if (!a.updated_at && !b.updated_at) return 0;
        if (!a.updated_at) return 1;
        if (!b.updated_at) return -1;
        return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
      },
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    ],
    []
  );

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
    setColumnConfig,
  } = useColumnVisibility(baseColumns);
  const maxVisibleColumns = 10;
  const { persistVisibleKeys } = useListingColumnsPersistence(
    listingKey,
    baseColumns,
    maxVisibleColumns,
    setColumnConfig
  );
  const { persistColumnWidths } = useListingColumnWidthsPersistence(
    listingKey,
    baseColumns,
    setColumnWidthByKey
  );
  const { persistSort } = useListingSortPersistence(
    listingKey,
    baseColumns,
    setSortKey,
    setSortOrder
  );
  const handleColumnVisibilityChangeLimited = useCallback(
    (keys: React.Key[]) => {
      if (keys.length > maxVisibleColumns) {
        message.warning(`You can select up to ${maxVisibleColumns} columns.`);
        return;
      }
      handleColumnVisibilityChange(keys);
      persistVisibleKeys(keys);
    },
    [handleColumnVisibilityChange, maxVisibleColumns, persistVisibleKeys]
  );

  const getSearchValueForColumn = useCallback((col: any) => {
    if (col.dataIndex === 'name') return (record: TaskTemplate) => record.name;
    if (col.dataIndex === 'task_type') {
      return (record: TaskTemplate) => {
        const type = record.taskType || (record as any).task_type;
        return type?.name || '';
      };
    }
    if (col.dataIndex === 'enabled') return (record: TaskTemplate) => (record.enabled ? 'Yes' : 'No');
    if (col.dataIndex === 'created_at') return (record: TaskTemplate) => formatDateValue(record.created_at);
    if (col.dataIndex === 'updated_at') return (record: TaskTemplate) => formatDateValue(record.updated_at);
    const key = String(col.key || '').toLowerCase();
    if (typeof col.dataIndex === 'string') {
      const dataIndex = col.dataIndex.toLowerCase();
      if (dataIndex.includes('date') || dataIndex.includes('due')) {
        return (record: TaskTemplate) => formatDateValue((record as any)[col.dataIndex]);
      }
    }
    if (key.includes('date') || key.includes('due')) {
      return (record: TaskTemplate) => formatDateValue((record as any)[col.key]);
    }
    return undefined as unknown as (record: TaskTemplate) => string | number | null | undefined;
  }, []);

  const searchColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        getSearchValue: getSearchValueForColumn(col),
      })),
    [visibleColumns, getSearchValueForColumn]
  );

  const memoizedColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        width: col.width,
        getSearchValue: getSearchValueForColumn(col),
      })),
    [visibleColumns, getSearchValueForColumn]
  );

  const { columnsWithSearch, filteredData, searchRowCells } = useTableColumnSearch<TaskTemplate>(
    taskTemplates,
    memoizedColumns as any,
    searchColumns as any,
    { splitHeader: true, listingKey }
  );

  const tableColumns = columnsWithSearch as any[];
  const totalValue = typeof totalCount === 'number' ? totalCount : filteredData.length;
  const totalLabel = `Total: ${totalValue} ${totalValue === 1 ? 'task template' : 'task templates'}`;
  const totalColumnCount = tableColumns.length + 1;
  const minColumnWidth = 50;
  const baseColumnWidths = useMemo(
    () =>
      tableColumns.map((col) => {
        const key = String(col.key);
        const cached = columnWidthByKey[key];
        if (typeof cached === 'number') {
          return cached;
        }
        if (typeof col.width === 'number') {
          return col.width;
        }
        if (typeof col.width === 'string') {
          const parsed = Number.parseFloat(col.width);
          if (Number.isFinite(parsed)) {
            return parsed;
          }
        }
        return 150;
      }),
    [tableColumns, columnWidthByKey]
  );
  const columnWidths = useMemo(() => {
    if (availableWidth == null) {
      return baseColumnWidths;
    }
    const total = baseColumnWidths.reduce((sum, width) => sum + width, 0) || 1;
    if (total <= availableWidth) {
      return baseColumnWidths;
    }
    const scale = availableWidth / total;
    return baseColumnWidths.map((width) => Math.max(minColumnWidth, Math.floor(width * scale)));
  }, [baseColumnWidths, availableWidth]);
  const handleResize = useCallback(
    (targetIndex: number) => (_e: unknown, data: { size: { width: number } }) => {
      const keys = tableColumns.map((col) => String(col.key));
      const current = columnWidths.map((width) => Math.max(minColumnWidth, Math.round(width)));
      const totalAvailable = availableWidth ?? current.reduce((sum, width) => sum + width, 0);
      const rightIndexes = current.map((_width, index) => index).filter((index) => index > targetIndex);
      if (rightIndexes.length === 0) {
        return;
      }
      const rightReducible = rightIndexes.reduce(
        (sum, index) => sum + Math.max(0, current[index] - minColumnWidth),
        0
      );
      const maxTarget = Math.max(minColumnWidth, current[targetIndex] + rightReducible);
      const nextWidth = Math.min(Math.max(Math.round(data.size.width), minColumnWidth), maxTarget);
      const delta = nextWidth - current[targetIndex];
      if (delta === 0) {
        return;
      }

      const updated = [...current];
      updated[targetIndex] = nextWidth;

      if (delta > 0) {
        const reducible = rightIndexes.map((index) => Math.max(0, current[index] - minColumnWidth));
        let remaining = delta;
        const totalReducible = reducible.reduce((sum, value) => sum + value, 0);
        if (totalReducible === 0) {
          return;
        }
        rightIndexes.forEach((index, position) => {
          const room = reducible[position];
          if (room === 0) return;
          const take = Math.min(room, Math.round((delta * room) / totalReducible));
          updated[index] = current[index] - take;
          remaining -= take;
        });
        if (remaining > 0) {
          for (let idx = 0; idx < rightIndexes.length && remaining > 0; idx += 1) {
            const index = rightIndexes[idx];
            if (updated[index] > minColumnWidth) {
              updated[index] -= 1;
              remaining -= 1;
            }
          }
        }
      } else {
        const add = -delta;
        const totalRight = rightIndexes.reduce((sum, index) => sum + current[index], 0);
        let remaining = add;
        rightIndexes.forEach((index) => {
          const give = Math.round((add * current[index]) / (totalRight || 1));
          updated[index] = current[index] + give;
          remaining -= give;
        });
        if (remaining !== 0) {
          for (let idx = 0; idx < rightIndexes.length && remaining !== 0; idx += 1) {
            const index = rightIndexes[idx];
            updated[index] += remaining > 0 ? 1 : -1;
            remaining += remaining > 0 ? -1 : 1;
          }
        }
      }

      const nextWidths: Record<string, number> = {};
      keys.forEach((key, index) => {
        nextWidths[key] = updated[index];
      });
      setColumnWidthByKey((prev) => {
        const merged = { ...prev, ...nextWidths };
        persistColumnWidths(merged);
        return merged;
      });
    },
    [availableWidth, columnWidths, minColumnWidth, persistColumnWidths, tableColumns]
  );

  useEffect(() => {
    const updateAvailableWidth = () => {
      const containerWidth = tableScrollRef.current?.clientWidth ?? 0;
      const borderAdjust = Math.max(totalColumnCount - 1, 0);
      const nextAvailable = Math.max(containerWidth - 48 - borderAdjust, 0);
      setAvailableWidth(nextAvailable || null);
    };

    updateAvailableWidth();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateAvailableWidth);
      return () => window.removeEventListener('resize', updateAvailableWidth);
    }
    const observer = new ResizeObserver(updateAvailableWidth);
    if (tableScrollRef.current) {
      observer.observe(tableScrollRef.current);
    }
    return () => observer.disconnect();
  }, [totalColumnCount]);

  useEffect(() => {
    const container = tableScrollRef.current;
    if (!container) {
      return;
    }
    const updateHeight = () => {
      const rect = container.getBoundingClientRect();
      const nextHeight = Math.max(window.innerHeight - rect.top - 24, 200);
      setViewportHeight(nextHeight);
      const nextScrollbarWidth = Math.max(container.offsetWidth - container.clientWidth, 0);
      setScrollbarWidth(nextScrollbarWidth);
    };
    updateHeight();
    const mutationObserver = new MutationObserver(() => {
      updateHeight();
    });
    mutationObserver.observe(container, { childList: true, subtree: true, attributes: true });
    const handleScroll = () => {
      setScrollTop(container.scrollTop);
    };
    container.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', updateHeight);
    return () => {
      mutationObserver.disconnect();
      container.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateHeight);
    };
  }, [filteredData.length]);


  const sortedData = useMemo(() => {
    if (!sortKey || !sortOrder) {
      return filteredData;
    }
    const column = tableColumns.find((col: any) => String(col.key) === sortKey);
    const sorter = column?.sorter;
    if (typeof sorter !== 'function') {
      return filteredData;
    }
    const sorted = [...filteredData].sort(sorter);
    return sortOrder === 'descend' ? sorted.reverse() : sorted;
  }, [filteredData, tableColumns, sortKey, sortOrder]);

  const rowHeight = 44;
  const { focusedRowId, handleTableKeyDown, handleRowClick } = useListingTableFocus<TaskTemplate>({
    rows: sortedData,
    getRowId: (row) => row.id,
    containerRef: tableScrollRef,
    rowHeight,
  });

  const overscan = 6;
  const totalRows = sortedData.length;
  const visibleRange = useMemo(() => {
    if (!viewportHeight || totalRows === 0) {
      return { start: 0, end: totalRows, topPad: 0, bottomPad: 0 };
    }
    const maxStart = Math.max(0, totalRows - 1);
    const rawStart = Math.min(
      maxStart,
      Math.max(0, Math.floor(scrollTop / rowHeight) - overscan)
    );
    const rawEnd = Math.min(
      totalRows,
      Math.ceil((scrollTop + viewportHeight) / rowHeight) + overscan
    );
    const start = Math.min(rawStart, totalRows);
    const end = Math.max(start, rawEnd);
    const topPad = start * rowHeight;
    const bottomPad = Math.max(0, (totalRows - end) * rowHeight);
    return { start, end, topPad, bottomPad };
  }, [overscan, rowHeight, scrollTop, totalRows, viewportHeight]);
  const visibleRows = useMemo(
    () => sortedData.slice(visibleRange.start, visibleRange.end),
    [sortedData, visibleRange]
  );

  const handleSort = (key: string, sorter?: unknown) => {
    if (typeof sorter !== 'function') {
      return;
    }
    if (sortKey !== key) {
      setSortKey(key);
      setSortOrder('ascend');
      persistSort(key, 'ascend');
      return;
    }
    if (sortOrder === 'ascend') {
      setSortOrder('descend');
      persistSort(key, 'descend');
      return;
    }
    setSortKey(null);
    setSortOrder(null);
    persistSort(null, null);
  };

  const getCellValue = (record: TaskTemplate, col: any) => {
    if (!col.dataIndex) {
      return undefined;
    }
    if (Array.isArray(col.dataIndex)) {
      return col.dataIndex.reduce((acc: any, key: string) => (acc == null ? undefined : acc[key]), record);
    }
    return (record as any)[col.dataIndex];
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div />
          <Space>
            <ColumnVisibilityToggle
              columns={columnConfig}
              visibleKeys={visibleKeys}
              onChange={handleColumnVisibilityChangeLimited}
              maxVisible={maxVisibleColumns}
            />
          {canDelete && (selectAll || selectedRowKeys.length > 0) && (
            <Popconfirm
              title={selectAll ? 'Delete all task templates?' : `Delete ${selectedRowKeys.length} task template${selectedRowKeys.length === 1 ? '' : 's'}?`}
              okText="Delete"
              okButtonProps={{ danger: true, loading: deleting }}
              onConfirm={handleBatchDelete}
            >
                <Button danger loading={deleting}>
                  Batch Delete
                </Button>
              </Popconfirm>
            )}
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                New Task Template
              </Button>
            )}
          </Space>
        </div>

        <div className="flowbite-listing-table-shell">
          <div style={{ paddingRight: scrollbarWidth }}>
            <FlowbiteTable className="flowbite-listing-table header-only w-full table-fixed" hoverable>
              <colgroup>
                <col style={{ width: 48 }} />
                {tableColumns.map((col, index) => (
                  <col key={`head_${String(col.key)}`} style={{ width: columnWidths[index] }} />
                ))}
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableHeadCell className="w-12">
                    <Checkbox
                      checked={selectAll || (sortedData.length > 0 && selectedRowKeys.length === sortedData.length)}
                      onClick={(event) => event.stopPropagation()}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectAll(true);
                          setExcludedRowKeys([]);
                          setSelectedRowKeys([]);
                        } else {
                          setSelectAll(false);
                          setExcludedRowKeys([]);
                          setSelectedRowKeys([]);
                        }
                      }}
                    />
                  </TableHeadCell>
                  {tableColumns.map((col, index) => {
                    const key = String(col.key);
                    const isSortable = typeof col.sorter === 'function';
                    const isSorted = sortKey === key;
                    const sortIndicator =
                      isSorted && sortOrder
                        ? sortOrder === 'ascend'
                          ? '▲'
                          : '▼'
                        : '';
                    const isFirstDataColumn = index === 0;
                    const maxWidth =
                      availableWidth != null
                        ? Math.max(minColumnWidth, availableWidth - minColumnWidth * (columnWidths.length - 1))
                        : undefined;

                    return (
                      <FlowbiteResizableHeader
                        key={key}
                        width={columnWidths[index]}
                        maxWidth={maxWidth}
                        onResize={handleResize(index)}
                        onClick={() => handleSort(key, col.sorter)}
                        className={isSortable ? 'cursor-pointer select-none' : undefined}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div>{col.title}</div>
                            {isFirstDataColumn && (
                              <div className="mt-1 text-xs text-gray-400">{totalLabel}</div>
                            )}
                          </div>
                          {isSortable && (
                            <span className="text-xs text-gray-400">{sortIndicator}</span>
                          )}
                        </div>
                      </FlowbiteResizableHeader>
                    );
                  })}
                </TableRow>
                <TableRow>
                  <TableHeadCell className="w-12" />
                  {tableColumns.map((col, index) => {
                    const key = String(col.key);
                    return (
                      <TableHeadCell key={`${key}-search`} className="py-2">
                        {searchRowCells?.[index] ?? null}
                      </TableHeadCell>
                    );
                  })}
                </TableRow>
              </TableHead>
            </FlowbiteTable>
          </div>
          <div
            className="flowbite-listing-table-scroll"
            ref={tableScrollRef}
            tabIndex={0}
            onKeyDown={handleTableKeyDown}
            style={
              viewportHeight
                ? {
                    height: viewportHeight,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                  }
                : { overflowY: 'auto', overflowX: 'hidden' }
            }
          >
            <FlowbiteTable className="flowbite-listing-table w-full table-fixed" hoverable>
              <colgroup>
                <col style={{ width: 48 }} />
                {tableColumns.map((col, index) => (
                  <col key={`body_${String(col.key)}`} style={{ width: columnWidths[index] }} />
                ))}
              </colgroup>
              <TableBody className="divide-y">
                {loading && (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length + 1} className="py-8 text-center text-sm text-gray-500">
                      Loading...
                    </TableCell>
                  </TableRow>
                )}
                {!loading && sortedData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={tableColumns.length + 1} className="py-8 text-center text-sm text-gray-500">
                      No task templates
                    </TableCell>
                  </TableRow>
                )}
              {!loading && sortedData.length > 0 && (
                <>
                  {visibleRange.topPad > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={tableColumns.length + 1}
                        style={{ height: visibleRange.topPad, padding: 0 }}
                      />
                    </TableRow>
                  )}
                  {visibleRows.map((record: TaskTemplate, index: number) => {
                    const isSelected = selectedRowKeys.includes(record.id);
                    const isFocused = focusedRowId === record.id;
                    return (
                      <TableRow
                        key={record.id}
                        data-row-id={record.id}
                        className={`hover:bg-gray-50 ${isFocused ? 'bg-blue-50' : ''}`}
                        onDoubleClick={canView ? () => handleEdit(record) : undefined}
                        onClick={() => handleRowClick(record.id)}
                      >
                        <TableCell className="w-12">
                          <Checkbox
                            checked={selectAll ? !excludedRowKeys.includes(record.id) : isSelected}
                            onClick={(event) => event.stopPropagation()}
                            onChange={() => {
                              if (selectAll) {
                                setExcludedRowKeys((prev) =>
                                  prev.includes(record.id)
                                    ? prev.filter((key) => key !== record.id)
                                    : [...prev, record.id]
                                );
                                return;
                              }
                              setSelectedRowKeys((prev) =>
                                prev.includes(record.id)
                                  ? prev.filter((key) => key !== record.id)
                                  : [...prev, record.id]
                              );
                            }}
                          />
                        </TableCell>
                        {tableColumns.map((col, index) => {
                          const value = getCellValue(record, col);
                          const content =
                            typeof col.render === 'function'
                              ? col.render(value, record, index)
                              : value ?? '-';
                          return (
                            <TableCell key={`${record.id}_${String(col.key)}`} style={{ width: columnWidths[index] }}>
                              {content}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                  {visibleRange.bottomPad > 0 && (
                    <TableRow>
                      <TableCell
                        colSpan={tableColumns.length + 1}
                        style={{ height: visibleRange.bottomPad, padding: 0 }}
                      />
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </FlowbiteTable>
        </div>
      </div>
    </div>
  );
};
